import { Suspense } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Transition } from '@headlessui/react';

import { FeatureFlagProvider } from '../contexts/FeatureFlagContext';

import { defaultTransitionProps } from '../v2/lib/animation';

import PatientHeader from '../v2/components/Headers/PatientHeader';
import { usePatientAuth } from '../contexts/PatientAuthContext';
import { useEffect } from 'react';
import classNames from 'classnames';
import { getClientLoginUrlWithRedirect } from '../v2/lib/url';
import { logger } from '../lib/logger';
import HomecomingLoader from '../v2/components/Loading/HomecomingLoader';
import ChunkLoadErrorBoundary, {
  ChunkLoadSettleSignal,
} from '../v2/components/ErrorHandling/ChunkLoadErrorBoundary';

const HIDE_HEADER_ROUTES = ['/client/intake'];
const UNAUTHED_ROUTES = ['/login-token', '/login/client/verify'];

function AuthedPatientContainer() {
  const navigate = useNavigate();
  const location = useLocation();

  const { patientTokenPayload, authedPatient, refreshAuthedPatient } =
    usePatientAuth();

  // Add logging to see the current path and authentication state
  logger.debug('AuthedPatientContainer rendering with:', {
    path: location.pathname,
    patientTokenPayload: !!patientTokenPayload,
    authedPatient: !!authedPatient,
  });

  const loadInitialAuthedPatient = async () => {
    try {
      await refreshAuthedPatient();
    } catch (error) {
      // TODO: Test if this is necessary / works
      if (error && error.message !== 'Invalid bearer token') {
        // Don't show an error message for invalid token errors.
        navigate('/login/client');
        // logout();
      }
    }
  };

  useEffect(() => {
    if (patientTokenPayload && !authedPatient) {
      // This will occur upon a page refresh.
      loadInitialAuthedPatient();
    }
  }, [patientTokenPayload, authedPatient]);

  // Add logging before the UNAUTHED_ROUTES check
  logger.debug('Checking UNAUTHED_ROUTES:', {
    pathname: location.pathname,
    UNAUTHED_ROUTES,
    matchesAny: UNAUTHED_ROUTES.some((route) =>
      location.pathname.includes(route),
    ),
    patientTokenExists: !!patientTokenPayload,
  });

  // Special case here for unauthed routes like /login-token and /login/client/verify
  // Because these routes need to be accessible without authentication
  if (UNAUTHED_ROUTES.some((route) => location.pathname.includes(route))) {
    logger.debug('Matched UNAUTHED_ROUTE, returning <Outlet />');
    return (
      <ChunkLoadErrorBoundary>
        <Suspense fallback={<HomecomingLoader fullScreenMode={false} />}>
          <ChunkLoadSettleSignal />
          <Outlet />
        </Suspense>
      </ChunkLoadErrorBoundary>
    );
  }

  if (!patientTokenPayload) {
    logger.debug(
      'No patient token payload, redirecting to login',
      patientTokenPayload,
    );

    // Extract prefillEmail from the URL if it exists
    const prefillEmail = new URLSearchParams(location.search).get(
      'prefillEmail',
    );
    const loginUrl = getClientLoginUrlWithRedirect(
      location,
      undefined,
      prefillEmail || undefined,
    );

    logger.debug('Redirecting to login URL:', loginUrl);
    return <Navigate to={loginUrl} replace />;
  }

  const hideHeader = HIDE_HEADER_ROUTES.some((route) =>
    location.pathname.startsWith(route),
  );

  return (
    <FeatureFlagProvider>
      <Transition
        show={Boolean(authedPatient)}
        {...defaultTransitionProps}
        className="relative"
      >
        <>
          {!hideHeader && <PatientHeader />}
          <main
            className={classNames(!hideHeader && 'pt-[var(--top-nav-height)]')}
          >
            {/* Client-portal pages are lazy-loaded (code-split), so show the
            branded loader while their chunk downloads. See Sentry WEB-P. */}
            <ChunkLoadErrorBoundary>
              <Suspense fallback={<HomecomingLoader fullScreenMode={false} />}>
                <ChunkLoadSettleSignal />
                <Outlet />
              </Suspense>
            </ChunkLoadErrorBoundary>
          </main>
        </>
      </Transition>

      {!authedPatient && <HomecomingLoader />}
    </FeatureFlagProvider>
  );
}

export default AuthedPatientContainer;
